import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { ADMIN_SECURITY_EVENTS, FORCE_SIGN_OUT } from "../gql/security";
import { Spacing } from "../types/enum";

interface SecurityEvent {
  id: string;
  type: string;
  ipAddress?: string | null;
  userAgent?: string | null;
  createdAt: string;
}

const EVENT_LABELS: Record<string, string> = {
  password_changed: "Password changed",
  password_reset: "Password reset via email",
  session_force_revoked: "A device was signed out",
  sessions_revoked_all: "All devices signed out",
  banned_login_blocked: "Blocked login attempt (suspended account)",
  refresh_with_revoked_token: "Refresh attempt with revoked token",
  csrf_rejected: "Cross-site request rejected",
  totp_removed: "Two-factor device removed",
  impersonation_started: "Admin impersonation started",
  logout_all: "Signed out of other devices",
};

/**
 * Admin member-detail security card: force sign-out (destroys all live
 * sessions + refresh tokens) and the account's security audit trail
 * (specs/revoke-token.md §9).
 */
export default function UserPageSecurityCard({ userId }: { userId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { data, loading, refetch } = useQuery<{
    adminSecurityEvents: SecurityEvent[];
  }>(ADMIN_SECURITY_EVENTS, {
    variables: { userId, limit: 20 },
    fetchPolicy: "network-only",
  });
  const [forceSignOut, { loading: signingOut }] = useMutation(FORCE_SIGN_OUT);

  const onForceSignOut = async () => {
    try {
      await forceSignOut({ variables: { userId } });
      await refetch();
      enqueueSnackbar("All devices signed out", { variant: "success" });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Unable to sign out devices", {
        variant: "error",
      });
    }
    setConfirmOpen(false);
  };

  const events = data?.adminSecurityEvents ?? [];

  return (
    <Paper sx={{ p: Spacing.m }}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography variant="h6" color="textPrimary">
          Security
        </Typography>
        <Button color="warning" disabled={signingOut} onClick={() => setConfirmOpen(true)}>
          Force sign out
        </Button>
      </Box>
      <Typography variant="body2" color="textSecondary">
        Signs the member out of every device immediately (sessions and refresh tokens).
      </Typography>

      <Divider sx={{ my: Spacing.m }} />

      <Typography variant="subtitle2" color="textPrimary">
        Recent security events
      </Typography>
      {loading ? (
        <CircularProgress size={20} sx={{ mt: Spacing.sm }} />
      ) : events.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No security events recorded.
        </Typography>
      ) : (
        events.map((event) => (
          <Box key={event.id} sx={{ py: Spacing.xs }}>
            <Typography variant="body2" color="textPrimary">
              {EVENT_LABELS[event.type] ?? event.type}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {new Date(event.createdAt).toLocaleString()}
              {event.ipAddress ? ` · ${event.ipAddress}` : ""}
            </Typography>
          </Box>
        ))
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Force sign out?</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            The member will be signed out of all devices on their next request and will need to log
            in again.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="warning" variant="contained" onClick={onForceSignOut}>
            Sign out all devices
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
